import type { ProductCardProps } from "@/components/ProductCard/types";
import { getCompletionTimeText } from "@/components/NewProductCard/utils/getCompletionTimeText";
import { getPacingText } from "@/components/NewProductCard/utils/getPacingText";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import type { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import { getWebContext } from "@/utils/tagular/helpers";
import type { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";

export type TagularPageType = "Homepage" | "Detail-Page";

/**
 * Merges Cohesion webContext with explicit page surface for Tagular (EDUN-10033).
 * Mirrors Industry Highlights / GTM carousel patterns.
 */
export function buildRecentlyViewedMergedWebContext(
  tagularPageType: TagularPageType,
): ElementViewed["webContext"] | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const base = getWebContext();
  return {
    ...base,
    userAgent: base?.userAgent ?? undefined,
    page: {
      ...base?.page,
      title: document.title,
      url: window.location.href,
      pageType: tagularPageType,
    },
  };
}

type CardPropsForDimensions = Pick<
  ProductCardProps,
  "weeksToComplete" | "level" | "pacing" | "attributes"
>;

/**
 * Returns a human-readable duration string for a given weeksToComplete value,
 * or undefined when the value is absent or non-positive.
 */
export function getDurationDimension(
  weeksToComplete: number | null | undefined,
): string | undefined {
  if (weeksToComplete == null || weeksToComplete <= 0) return undefined;
  return getCompletionTimeText(weeksToComplete, false) ?? undefined;
}

/**
 * Eventing guide: customDimension keys duration, level, flexibility, skill.
 * Precedent: IndustryHighlightsCardsSlider (same four keys).
 */
export function buildRecentlyViewedProductCustomDimensions(
  props: CardPropsForDimensions,
): ProductClicked["customDimensions"] {
  const duration = getDurationDimension(props.weeksToComplete);

  const level = props.level;

  const flexibility =
    getPacingText(props.pacing ?? undefined) ?? props.pacing ?? undefined;

  const skill =
    props.attributes && props.attributes.length > 0
      ? props.attributes.join(", ")
      : undefined;

  return createCustomDimensions([
    ["duration", duration],
    ["level", level],
    ["flexibility", flexibility],
    ["skill", skill],
  ]);
}
